import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type {
  InvestorListDTO,
  TransactionImportPreviewRowDTO,
  TransactionImportRoomOptionDTO,
  TransactionImportRowOverrideDTO,
} from '@inversor-hub/types';
import { PreviewTableRow } from './PreviewTableRow';
import type { ImportRowStatus } from './types';

export interface PreviewTableRowEntry {
  row: TransactionImportPreviewRowDTO;
  override: TransactionImportRowOverrideDTO | undefined;
  status: ImportRowStatus;
}

interface PreviewTableProps {
  entries: PreviewTableRowEntry[];
  investments: InvestorListDTO[];
  roomOptions: TransactionImportRoomOptionDTO[];
  showInvestmentColumn: boolean;
  showRoomColumn: boolean;
  roomRentInvestmentIds: string[];
  isLoading?: boolean;
  onRowOverrideChange: (
    rowNumber: number,
    patch: Omit<TransactionImportRowOverrideDTO, 'rowNumber'>
  ) => void;
}

export const PreviewTable = ({
  entries,
  investments,
  roomOptions,
  showInvestmentColumn,
  showRoomColumn,
  roomRentInvestmentIds,
  isLoading,
  onRowOverrideChange,
}: PreviewTableProps) => {
  if (entries.length === 0 && !isLoading) {
    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
          textAlign: 'center',
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No hay filas que coincidan con el filtro seleccionado.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          flex: 1,
          minHeight: 0,
          borderRadius: 1,
          transition: 'opacity 150ms ease',
          opacity: isLoading ? 0.5 : 1,
          pointerEvents: isLoading ? 'none' : undefined,
          '& thead th': {
            bgcolor: 'background.paper',
            fontWeight: 600,
            color: 'text.secondary',
            whiteSpace: 'nowrap',
          },
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 32, p: 0 }} />
              <TableCell sx={{ width: 48 }}>#</TableCell>
              <TableCell sx={{ width: 56 }}>Estado</TableCell>
              <TableCell sx={{ width: 96 }}>Fecha</TableCell>
              <TableCell sx={{ width: 80 }}>Tipo</TableCell>
              {showInvestmentColumn ? (
                <TableCell sx={{ width: 220, minWidth: 200 }}>Inversión</TableCell>
              ) : null}
              <TableCell>Concepto</TableCell>
              {showRoomColumn && (
                <TableCell sx={{ width: 230, minWidth: 210 }}>Habitación</TableCell>
              )}
              <TableCell sx={{ width: 112, textAlign: 'right' }}>Importe</TableCell>
              <TableCell sx={{ width: 200 }}>Categoría</TableCell>
              <TableCell sx={{ width: 48 }} aria-label="Acciones" />
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((entry) => (
              <PreviewTableRow
                key={entry.row.rowNumber}
                row={entry.row}
                override={entry.override}
                status={entry.status}
                investments={investments}
                roomOptions={roomOptions}
                showInvestmentColumn={showInvestmentColumn}
                showRoomColumn={showRoomColumn}
                showRoomForRow={
                  !(entry.override?.investmentId ?? entry.row.investmentMatch.investmentId) ||
                  roomRentInvestmentIds.includes(
                    entry.override?.investmentId ?? entry.row.investmentMatch.investmentId ?? ''
                  )
                }
                onRowOverrideChange={onRowOverrideChange}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            pointerEvents: 'none',
          }}
        >
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Actualizando preview…
          </Typography>
        </Box>
      )}
    </Box>
  );
};
